import dayjs from "dayjs";
import { z } from "zod";

/**
 * Handles caching data locally with expiration and validation.
 */
export default class LocalStorageSvc {
  static getItem<T>(
    key: string,
    resolver: z.ZodType<T>,
    defaultData?: T
  ): T | null {
    const item = localStorage.getItem(key);
    if (!item) {
      return defaultData || null;
    }

    const schema = z.object({
      data: resolver,
      expiresAt: z.number(),
    });

    let json;
    try {
      json = JSON.parse(item);
    } catch {
      console.error(`Item ${key} in local storage is not a valid JSON`);
      return defaultData || null;
    }

    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      console.error(`Item ${key} in local storage is not the expected type`);
      return defaultData || null;
    }

    if (parsed.data.expiresAt < Date.now()) {
      console.warn(`Item ${key} in local storage has expired`);
      localStorage.removeItem(key);
      return defaultData || null;
    }

    if (!parsed.data.data) {
      console.error(`Item ${key} in local storage is missing data`);
      return defaultData || null;
    }

    return parsed.data.data;
  }

  static setItem(
    key: string,
    data: unknown,
    expiresInMinutes: number = 60 * 24 * 30 // 30 days default
  ) {
    const expiresAt = dayjs().add(expiresInMinutes, "minute").unix() * 1000;
    const item = { data, expiresAt };
    localStorage.setItem(key, JSON.stringify(item));
  }
}
